
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { DateTime } from 'luxon';
import { addBorderAroundTable, addStylingToHorizontalHeader, downloadFile, setCellFontSizeAndAlignement } from '@/utils/exceljs-helpers';
import * as ExcelJS from 'exceljs';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import DatePicker from '@/components/shared/DatePicker.vue';

@Component<CreditsChangeHistoryModal>({
	components: {
		StandardModal,
		TextButton,
		DatePicker
	}
})
export default class CreditsChangeHistoryModal extends Vue {
	@Action('getCreditsChangeHistory', { namespace: 'auth' }) private getChangeHistory!: (dateRange: string[]) => Promise<CreditTransaction[]>;
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;

	private dateRange: string[] = [];
	private isLoading: boolean = false;

	/**
	 * Function to check if the date can be selected in the date picker.
	 *
	 * @param {string} dateString - Date from the date picker
	 * @return {boolean} - Is the date between a year ago and today
	 */
	private getAllowedDates(dateString: string): boolean {
		const today = DateTime.local().startOf('day');
  		const oneYearAgo = today.minus({ years: 1 });
		const date = DateTime.fromISO(dateString);
		return date >= oneYearAgo && date <= today;
	};

	/**
	 * Fetch the change history and export it to a spreadsheet.
	 *
	 * @return {Promise<void>}
	 */
	private async exportHistory(): Promise<void> {
		// Error message if the date range is not complete
		if (this.dateRange.length < 2) {
			Toast.error(this.$t('settings.credits.change_history.no_date_range'));
			return;
		}

		this.isLoading = true;
		try {
			const changelogs: CreditTransaction[] = await this.getChangeHistory(this.dateRange);
			if (!changelogs.length) {
				Toast.error(this.$t('settings.credits.change_history.no_data'));
			}
			else {
				this.exportData(changelogs);
			}
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.credits.change_history.error_fetching', { errorMessage }));
		}
		finally {
			this.isLoading = false;
		}
	}

	/**
	 * Export the credits history to an Excel file.
	 *
	 * @param {CreditTransaction[]} changelogs
	 * @return {void}
	 */
	 private exportData(changelogs: CreditTransaction[]): void {
		const workbook = new ExcelJS.Workbook();

		const worksheet = workbook.addWorksheet(this.$t('settings.credits.change_history.title'), { pageSetup: { orientation: 'portrait', fitToPage: true } });

		worksheet.columns = [
			{ header: this.$t('settings.credits.change_history.export.date_column'), key: 'date', width: 30 },
			{ header: this.$t('settings.credits.change_history.export.email_column'), key: 'email', width: 40 },
			{ header: this.$t('settings.credits.change_history.export.activity_column'), key: 'action', width: 30 },
			{ header: this.$t('settings.credits.change_history.export.amount_column'), key: 'amount', width: 20 },
			{ header: this.$t('settings.credits.change_history.export.reason_column'), key: 'reason', width: 70 }
		];

		const activityData = changelogs.map((transaction) => {
			return {
				date: DateTime.fromISO(transaction.createdAt).setZone('UTC', { keepLocalTime: true }).toFormat(this.$t('settings.credits.change_history.export.date_format')),
				email: transaction.email,
				action: transaction.action,
				amount: this.$n(transaction.amount, 'currency'),
				reason: transaction.reason
			};
		});

		worksheet.addRows(activityData);

		setCellFontSizeAndAlignement(worksheet, 14, { vertical: 'top', horizontal: 'left' });
		addStylingToHorizontalHeader(worksheet);
		addBorderAroundTable(worksheet, 1, worksheet.rowCount);

		const startDate = DateTime.fromISO(this.dateRange[0]).toFormat(this.$t('settings.credits.change_history.date_format'));
		const endDate = DateTime.fromISO(this.dateRange[1]).toFormat(this.$t('settings.credits.change_history.date_format'));

		// Save the workbook to a file
		workbook.xlsx.writeBuffer().then(buffer => {
			downloadFile(buffer, this.$t('settings.credits.change_history.export.file_name', { startDate, endDate }));
		});
	}

	/**
	 * Emit event to close modal
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		this.$emit('close');
	}
}
